import React, { useEffect, useState, useRef, memo, useCallback } from "react";
import {
  useRoomContext,
  useConnectionState,
  useTracks,
  VideoTrack,
  AudioTrack,
  isTrackReference,
} from "@livekit/components-react";
import { ConnectionState, Track } from "livekit-client";
import EgressHelper from "@livekit/egress-sdk";
import cn from "classnames";
import { useLayoutStyles } from "../../hooks/useLayoutStyles";
import { StreamLayoutCanvasInnerProps } from "./StudioTypes";
import styles from "../StreamLayoutCanvas/StreamLayoutCanvas.module.css";
import { BackgroundRenderer } from "../../pages/Studio/BackgroundRenderer/BackgroundRenderer";
import { LogoRenderer } from "../../pages/Studio/RightButtons/LogoRenderer";

export const StreamLayoutCanvasInner = memo(({
  className,
  showLocalTracks = true,
  layout,
  currentTemplate,
  background,
  logo,
  onConnectionStateChange,
}: StreamLayoutCanvasInnerProps) => {
  const room = useRoomContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeTemplate, setActiveTemplate] = useState(currentTemplate);
  const [isScreenShareWithAudioActive, setIsScreenShareWithAudioActive] = useState(false);
  const connectionStatus = useConnectionState();

  const videoTracks = useTracks(
    [Track.Source.Camera, Track.Source.ScreenShare],
    { onlySubscribed: !showLocalTracks }
  );

  const audioTracks = useTracks(
    [Track.Source.Microphone, Track.Source.ScreenShareAudio],
    { onlySubscribed: !showLocalTracks }
  );

  const { getVideoPositionStyle } = useLayoutStyles(
    videoTracks,
    activeTemplate
  );

  const getLayoutClass = useCallback((): string => {
    return cn(
      styles["stream-layout__videos"],
      layout && styles[`stream-layout__videos--${layout}`]
    );
  }, [layout]);

  useEffect(() => {
    if (onConnectionStateChange) onConnectionStateChange(connectionStatus);
  }, [connectionStatus, onConnectionStateChange]);

  useEffect(() => {
    if (room) EgressHelper.setRoom(room);
    setActiveTemplate(currentTemplate);
  }, [room, currentTemplate]);


  useEffect(() => {
    if (!room || !room.localParticipant) return;

    const hasScreenShareWithAudio = !!room.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio);
    setIsScreenShareWithAudioActive(hasScreenShareWithAudio);
  }, [room]);

  const renderLoading = () => {
    let message = "Ожидание подключения...";

    if (connectionStatus === ConnectionState.Connecting) {
      message = "Подключение к потоку...";
    } else if (connectionStatus === ConnectionState.Reconnecting) {
      message = "Переподключение...";
    }

    return (
      <div className={styles["stream-layout__loading"]}>
        {message}
      </div>
    );
  };

  const renderVideoTracks = () => (
    <div className={cn(getLayoutClass(), styles["stream-layout__videos-container"])}>
      {videoTracks.filter(isTrackReference).map((trackRef) => {
        const isMuted = trackRef.publication?.isMuted || false;
        const trackSid = trackRef.publication?.trackSid;

        return (
          <div
            key={trackSid}
            className={cn(
              styles["stream-layout__video-container"],
              styles["stream-layout__video-track"],
              {
                [styles["stream-layout__video-track--muted"]]: isMuted,
                [styles["stream-layout__video-track--unmuted"]]: !isMuted
              }
            )}
            data-track-id={trackSid}
            style={getVideoPositionStyle(trackRef)}
          >
            <VideoTrack trackRef={trackRef} />
          </div>
        );
      })}
    </div>
  );

  const renderNoStreamsTemplate = () => (
    <div className={cn(
      styles["stream-layout__no-streams-template"],
      styles["stream-layout__no-streams-container"]
    )}
    />
  );

  const renderContent = () => (
    <div className={cn(
      styles["stream-layout__content"],
      styles["stream-layout__content-container"]
    )}>
      {videoTracks.length === 0
        ? renderNoStreamsTemplate()
        : renderVideoTracks()}

      {!isScreenShareWithAudioActive && audioTracks.filter(isTrackReference).map(track => (
        <AudioTrack
          key={track.publication?.trackSid}
          trackRef={track}
        />
      ))}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={cn(styles["stream-layout"], className)}
      data-layout={activeTemplate?.elements || layout}
    >
      <div className={styles["stream__logo"]}>
        {logo && <LogoRenderer logo={logo} />}
      </div>
      <div className={styles["stream-layout__background-container"]}>
        {background && <BackgroundRenderer background={background} />}
      </div>

      {connectionStatus !== ConnectionState.Connected
        ? renderLoading()
        : renderContent()}
    </div>
  );
});